import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ConfidenceMatrixInfo } from '../../components/common/ConfidenceMatrixInfo';
import { OutcomeDistributionBar } from '../../components/common/OutcomeDistributionBar';
import type { VersionedTrendPoint } from '../../components/common/VersionedTrendChart';
import { VersionedTrendChart } from '../../components/common/VersionedTrendChart';
import { DEFAULT_FORGE_CHAIN_ID, FORGE_CHAIN_OPTIONS } from '../../config/chains';
import {
  bucketDefinitionFor,
  bucketLabel,
  cellKey,
  RECOGNITION_CELL_LABELS,
  RECOGNITION_CELL_ORDER,
  RECOGNITION_CELL_TONE,
  subBucketLabel,
} from '../../constants/recognitionReview';
import {
  enumParam,
  nullableStringParam,
  numberParam,
  pageParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { FORGE_PR_INFERENCE_OKRS_PATH, FORGE_RECOGNITION_REVIEW_PATH } from '../../paths';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  RecognitionAdjustmentReason,
  RecognitionBucketParetoEntry,
  RecognitionCaptureUnitRow,
  RecognitionMatchState,
  RecognitionOkrAggregatesResponse,
  RecognitionOkrPopulation,
  RecognitionOutcomeCell,
  RecognitionOutcomeGrid,
  RecognitionSamplingStrategy,
} from '../../types';

const UNIT_PAGE_LIMIT = 25;
/** Weeks charted by the trends view, ending at the week the detail view is showing. */
const TREND_WEEK_COUNT = 8;
/** api-dl serves one week per request; keep the fan-out polite. */
const TREND_CONCURRENCY = 4;
/** Below this review completeness a week's rate is directional only, not an OKR reading. */
const PROVISIONAL_COMPLETENESS_PCT = 95;

const MATCH_STATES: readonly RecognitionMatchState[] = ['correct', 'adjusted', 'incorrect'];
const MATCH_STATE_LABELS: Record<RecognitionMatchState, string> = {
  correct: 'Correct',
  adjusted: 'Adjusted',
  incorrect: 'Incorrect',
};
const ADJUSTMENT_REASON_LABELS: Record<RecognitionAdjustmentReason, string> = {
  missing_scan: 'Missing scan',
  wrong_scan: 'Wrong scan',
  partial_groups: 'Partial groups',
  non_front_groups: 'Non-front groups',
};
const ADJUSTMENT_REASON_ORDER: readonly RecognitionAdjustmentReason[] = [
  'missing_scan',
  'wrong_scan',
  'partial_groups',
  'non_front_groups',
];
const SETTABLE_SAMPLING_STRATEGIES: readonly RecognitionSamplingStrategy[] = [
  'representative',
  'targeted',
];
const SAMPLING_STRATEGY_LABELS: Record<RecognitionSamplingStrategy, string> = {
  untagged: 'Untagged',
  representative: 'Representative',
  targeted: 'Targeted',
};
const POPULATION_OPTIONS: ReadonlyArray<{ value: RecognitionOkrPopulation; label: string }> = [
  { value: 'representative', label: 'Representative' },
  { value: 'all', label: 'All' },
];

// `chain` is a plain number, not `chainFilterParam`: the aggregates endpoint scores exactly one
// chain-week, so there is no meaningful "all chains" reading to fall back to.
const RECOGNITION_OKRS_URL_FILTERS = {
  chain: numberParam(DEFAULT_FORGE_CHAIN_ID),
  week: nullableStringParam(),
  view: enumParam(['detail', 'trends'] as const, 'detail'),
  population: enumParam(['representative', 'all'] as const, 'representative'),
  cell: nullableStringParam(),
  page: pageParam(),
};

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

/** `confidentCorrectRate` is a 0-1 fraction; every other percentage on the wire is already 0-100. */
function formatRate(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return '-';
  return `${(fraction * 100).toFixed(digits)}%`;
}

function formatPct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return `${value.toFixed(digits)}%`;
}

function parseYmd(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toYmd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Snap a `YYYY-MM-DD` date to the Monday that starts its week.
 *
 * Weeks are chain-local on the backend, but the boundary itself is a calendar Monday, so snapping
 * in UTC keeps the picker from drifting a day in negative-offset time zones.
 */
export function mondayOf(value: string): string | null {
  const date = parseYmd(value);
  if (!date) return null;
  const mondayIndexedWeekday = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - mondayIndexedWeekday);
  return toYmd(date);
}

export function shiftWeekStart(weekStart: string, weeks: number): string | null {
  const date = parseYmd(weekStart);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + weeks * 7);
  return toYmd(date);
}

/** The `count` week starts ending at (and including) `latestWeekStart`, oldest first. */
export function recentWeekStarts(latestWeekStart: string, count: number): string[] {
  const weeks: string[] = [];
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const weekStart = shiftWeekStart(latestWeekStart, -offset);
    if (weekStart) weeks.push(weekStart);
  }
  return weeks;
}

function lastCompletedWeekStart(now: Date = new Date()): string | null {
  const today = mondayOf(toYmd(now));
  return today ? shiftWeekStart(today, -1) : null;
}

function formatWeekRange(weekStart: string): string {
  const start = parseYmd(weekStart);
  if (!start) return weekStart;
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const format = (date: Date, withYear: boolean) =>
    new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    }).format(date);
  return `${format(start, false)} - ${format(end, true)}`;
}

/**
 * Share of the selected population that produced a scoreable Recognition confidence outcome.
 *
 * Coverage failures are samples the pipeline could not score at all (no inference, no shape), so
 * they are the denominator's other half rather than an outcome cell.
 */
export function coveragePct(grid: RecognitionOutcomeGrid): number | null {
  const attempted = grid.scoreable + grid.coverageFailures;
  return attempted > 0 ? (grid.scoreable / attempted) * 100 : null;
}

function cellCount(grid: RecognitionOutcomeGrid, cell: RecognitionOutcomeCell): number {
  return grid.cells[cell] ?? 0;
}

function isOutcomeCell(value: string | null): value is RecognitionOutcomeCell {
  return value !== null && (RECOGNITION_CELL_ORDER as readonly string[]).includes(value);
}

function unitKey(unit: Pick<RecognitionCaptureUnitRow, 'imageId' | 'modTag'>): string {
  return `${unit.imageId}:${unit.modTag ?? ''}`;
}

/**
 * Run `worker` over `items` with at most `limit` in flight, preserving input order.
 *
 * The trends view needs one request per charted week; a bounded pool keeps a chain-year of history
 * from opening eight simultaneous aggregate queries.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const runnerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(
    Array.from({ length: runnerCount }, async () => {
      for (;;) {
        const index = cursor;
        cursor += 1;
        if (index >= items.length) return;
        results[index] = await worker(items[index], index);
      }
    })
  );
  return results;
}

interface TrendWeek {
  weekStart: string;
  aggregates: RecognitionOkrAggregatesResponse;
}

function isProvisional(response: Pick<RecognitionOkrAggregatesResponse, 'reviewCompletenessPct'>) {
  const completeness = response.reviewCompletenessPct;
  return completeness === null || completeness < PROVISIONAL_COMPLETENESS_PCT;
}

/**
 * Version tuple handed to `VersionedTrendChart`.
 *
 * A week scored under a different confidence profile is not comparable to the current reading, so
 * the profile version segments the line on its own. Review completeness rides alongside it: weeks
 * below the completeness threshold segment away too and render dashed, which is exactly the
 * "provisional" treatment they need.
 */
function trendVersions(week: TrendWeek): Record<string, string | null> {
  return {
    profile: week.aggregates.confidenceProfileVersion,
    review: isProvisional(week.aggregates) ? 'provisional' : 'complete',
  };
}

function trendTimestamp(weekStart: string): string {
  return `${weekStart}T00:00:00Z`;
}

interface OutcomeGridCardProps {
  title: string;
  description: string;
  grid: RecognitionOutcomeGrid;
  isEmphasized?: boolean;
  selectedCell: RecognitionOutcomeCell | null;
  onSelectCell: (cell: RecognitionOutcomeCell) => void;
  note?: React.ReactNode;
  testId: string;
}

/** One 2x3 confidence-by-match-state grid, plus its stacked distribution bar. */
const OutcomeGridCard: React.FC<OutcomeGridCardProps> = ({
  title,
  description,
  grid,
  isEmphasized = false,
  selectedCell,
  onSelectCell,
  note,
  testId,
}) => (
  <div
    className={`recognition-okrs-grid-card${isEmphasized ? ' is-emphasized' : ''}`}
    data-testid={testId}
  >
    <div className="recognition-okrs-grid-heading">
      <span className="recognition-okrs-grid-title">{title}</span>
      <span className="recognition-okrs-grid-subtitle">{description}</span>
    </div>
    <table className="recognition-okrs-grid-table" aria-label={`${title} outcome grid`}>
      <thead>
        <tr>
          <th scope="col">
            <span className="forge-visually-hidden">Confidence</span>
          </th>
          {MATCH_STATES.map((matchState) => (
            <th key={matchState} scope="col">
              {MATCH_STATE_LABELS[matchState]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[true, false].map((confident) => (
          <tr key={confident ? 'confident' : 'unconfident'}>
            <th scope="row">{confident ? 'Confident' : 'Unconfident'}</th>
            {MATCH_STATES.map((matchState) => {
              const cell = cellKey(confident, matchState);
              const count = cellCount(grid, cell);
              const share = grid.scoreable > 0 ? (count / grid.scoreable) * 100 : null;
              return (
                <td key={cell}>
                  <button
                    type="button"
                    className={[
                      'recognition-okrs-grid-cell',
                      `is-${RECOGNITION_CELL_TONE[cell]}`,
                      selectedCell === cell ? 'is-selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-pressed={selectedCell === cell}
                    aria-label={`${RECOGNITION_CELL_LABELS[cell]} · ${title}`}
                    title={`${RECOGNITION_CELL_LABELS[cell]}: ${formatNumber(count)} (${formatPct(
                      share
                    )})`}
                    data-testid={`${testId}-cell-${cell}`}
                    onClick={() => onSelectCell(cell)}
                  >
                    <span className="recognition-okrs-grid-count">{formatNumber(count)}</span>
                    <span className="recognition-okrs-grid-share">{formatPct(share)}</span>
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    <OutcomeDistributionBar
      cells={grid.cells}
      total={grid.scoreable}
      ariaLabel={`${title} outcome distribution`}
      onSegmentClick={onSelectCell}
    />
    <div className="recognition-okrs-grid-footer">
      <span>
        {formatNumber(grid.scoreable)} scoreable · {formatNumber(grid.coverageFailures)} coverage
        failures · {formatNumber(grid.missingScan)} missing scan
      </span>
      {note}
    </div>
  </div>
);

/** One Pareto bar after the outcome-cell filter has been applied. */
interface ParetoRow {
  bucket: string;
  count: number;
  subBuckets: Array<[string, number]>;
}

function byCountThenName(
  left: readonly [string, number],
  right: readonly [string, number]
): number {
  return right[1] - left[1] || left[0].localeCompare(right[0]);
}

/**
 * Collapse the week's Pareto to the bars a `cell` filter leaves standing.
 *
 * With no filter this is the week-level reading the backend already ordered. With one, every bar is
 * recounted from `cell_counts` (and each sub-bar from `sub_bucket_cell_counts`), re-sorted, and
 * dropped when nothing in that bucket landed in the cell — the bar order under a filter is a
 * genuinely different ranking, not the unfiltered one with different numbers.
 */
export function paretoRowsForCell(
  entries: readonly RecognitionBucketParetoEntry[],
  cell: RecognitionOutcomeCell | null
): ParetoRow[] {
  if (cell === null) {
    return [...entries]
      .map((entry) => ({
        bucket: entry.bucket,
        count: entry.count,
        subBuckets: Object.entries(entry.sub_buckets ?? {}),
      }))
      .sort((left, right) => right.count - left.count || left.bucket.localeCompare(right.bucket));
  }
  return entries
    .map((entry) => ({
      bucket: entry.bucket,
      count: entry.cell_counts?.[cell] ?? 0,
      subBuckets: Object.entries(entry.sub_bucket_cell_counts ?? {})
        .map(([subBucket, counts]): [string, number] => [subBucket, counts?.[cell] ?? 0])
        .filter(([, count]) => count > 0)
        .sort(byCountThenName),
    }))
    .filter((row) => row.count > 0)
    .sort((left, right) => right.count - left.count || left.bucket.localeCompare(right.bucket));
}

interface BucketParetoProps {
  entries: RecognitionOkrAggregatesResponse['bucketPareto'];
  selectedCell: RecognitionOutcomeCell | null;
  expandedBuckets: ReadonlySet<string>;
  onToggleBucket: (bucket: string) => void;
}

const BucketPareto: React.FC<BucketParetoProps> = ({
  entries,
  selectedCell,
  expandedBuckets,
  onToggleBucket,
}) => {
  const sorted = useMemo(() => paretoRowsForCell(entries, selectedCell), [entries, selectedCell]);
  const total = sorted.reduce((sum, entry) => sum + entry.count, 0);
  const maxCount = sorted.reduce((max, entry) => Math.max(max, entry.count), 0);

  if (sorted.length === 0) {
    return (
      <div className="dg-empty-state" data-testid="recognition-okrs-pareto-empty">
        <strong>
          {selectedCell
            ? `No Recognition issue buckets for ${RECOGNITION_CELL_LABELS[selectedCell]}`
            : 'No Recognition issue buckets recorded this week'}
        </strong>
        {/* A confident correct sample needs no remediation, so reviewers never record a primary
            bucket for one: that cell is structurally empty rather than merely unpopulated. */}
        <span>
          {selectedCell === 'confident_correct'
            ? 'A confident correct sample needs no remediation, so reviewers record no primary bucket for it. Pick another outcome cell or clear the filter.'
            : selectedCell
              ? 'No reviewed verdict in this cell carries a primary bucket. Pick another outcome cell or clear the filter.'
              : 'No reviewed verdict this week carries a primary bucket.'}
        </span>
      </div>
    );
  }

  return (
    <ul className="recognition-okrs-pareto" data-testid="recognition-okrs-pareto">
      {sorted.map((entry) => {
        const isExpanded = expandedBuckets.has(entry.bucket);
        const subBuckets = entry.subBuckets;
        return (
          <li key={entry.bucket}>
            <button
              type="button"
              className="recognition-okrs-pareto-bar"
              aria-expanded={isExpanded}
              aria-label={`${bucketLabel(entry.bucket)}: ${formatNumber(entry.count)} ${
                entry.count === 1 ? 'sample' : 'samples'
              }${selectedCell ? ` · ${RECOGNITION_CELL_LABELS[selectedCell]}` : ''}`}
              disabled={subBuckets.length === 0}
              onClick={() => onToggleBucket(entry.bucket)}
            >
              <span className="recognition-okrs-pareto-label">
                {subBuckets.length > 0 ? (
                  isExpanded ? (
                    <ExpandLessOutlinedIcon fontSize="inherit" />
                  ) : (
                    <ExpandMoreOutlinedIcon fontSize="inherit" />
                  )
                ) : null}
                <strong>{bucketLabel(entry.bucket)}</strong>
                <em>{bucketDefinitionFor(entry.bucket)?.owner ?? 'Unassigned'}</em>
              </span>
              <span
                className="recognition-okrs-pareto-track"
                aria-hidden="true"
                style={
                  { '--share': maxCount > 0 ? entry.count / maxCount : 0 } as React.CSSProperties
                }
              >
                <span className="recognition-okrs-pareto-fill" />
              </span>
              <span className="recognition-okrs-pareto-count">
                {formatNumber(entry.count)}
                <em>{total > 0 ? formatPct((entry.count / total) * 100, 0) : '-'}</em>
              </span>
            </button>
            {isExpanded && subBuckets.length > 0 ? (
              <ul className="recognition-okrs-pareto-subs">
                {subBuckets.map(([subBucket, count]) => (
                  <li key={subBucket}>
                    <span>{subBucketLabel(entry.bucket, subBucket)}</span>
                    <strong>{formatNumber(count)}</strong>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export const RecognitionOkrsDashboard: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [filters, updateFilters] = useUrlFilterState(RECOGNITION_OKRS_URL_FILTERS);
  const { chain: chainId, week: weekFilter, view, population, cell: rawCell, page } = filters;
  const selectedCell = isOutcomeCell(rawCell) ? rawCell : null;

  const [aggregates, setAggregates] = useState<RecognitionOkrAggregatesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [latestWeekStart, setLatestWeekStart] = useState<string | null>(null);
  const [expandedBuckets, setExpandedBuckets] = useState<ReadonlySet<string>>(() => new Set());
  const [savingUnitKey, setSavingUnitKey] = useState<string | null>(null);
  const [trendWeeks, setTrendWeeks] = useState<TrendWeek[] | null>(null);
  const [isTrendsLoading, setIsTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);
  const loadRequestSequence = useRef(0);
  const trendRequestSequence = useRef(0);

  const loadAggregates = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const response = await ForgeApiService.fetchRecognitionOkrAggregates(token, {
        chainId,
        weekStart: weekFilter,
        population,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setAggregates(response);
      setLoadError(null);
      // The backend resolves the default week to the last completed one, so an unfiltered response
      // is the only place the newest reviewable week is announced.
      if (!weekFilter) setLatestWeekStart(response.weekStart);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Recognition OKRs';
      setAggregates(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) setIsLoading(false);
    }
  }, [chainId, population, showErrorToast, token, weekFilter]);

  useEffect(() => {
    void loadAggregates();
  }, [loadAggregates]);

  const effectiveWeek = weekFilter ?? aggregates?.weekStart ?? null;
  const newestWeek = latestWeekStart ?? lastCompletedWeekStart();
  // Changing week, population, or chain repaints the label and the URL immediately, so the numbers
  // underneath them are last week's until the refetch lands. Keeping them mounted is right - a
  // blank page loses the reader's place - but they have to read as stale rather than as current.
  const isRefreshingWeek = isLoading && Boolean(aggregates);

  const loadTrends = useCallback(async () => {
    if (view !== 'trends' || !effectiveWeek) return;
    const requestSequence = trendRequestSequence.current + 1;
    trendRequestSequence.current = requestSequence;
    setIsTrendsLoading(true);
    setTrendsError(null);
    const weekStarts = recentWeekStarts(effectiveWeek, TREND_WEEK_COUNT);
    // Individual weeks are allowed to fail: a partial history is still a usable trend, and one bad
    // week must not blank the whole view.
    const responses = await mapWithConcurrency(weekStarts, TREND_CONCURRENCY, (weekStart) =>
      ForgeApiService.fetchRecognitionOkrAggregates(token, {
        chainId,
        weekStart,
        population,
      }).then(
        (aggregate): TrendWeek | null => ({ weekStart, aggregates: aggregate }),
        () => null
      )
    );
    if (trendRequestSequence.current !== requestSequence) return;
    const loaded = responses.filter((week): week is TrendWeek => week !== null);
    setTrendWeeks(loaded);
    setIsTrendsLoading(false);
    if (loaded.length === 0) {
      setTrendsError('No Recognition OKR weeks could be loaded.');
    } else if (loaded.length < weekStarts.length) {
      setTrendsError(
        `${weekStarts.length - loaded.length} of ${weekStarts.length} weeks failed to load.`
      );
    }
  }, [chainId, effectiveWeek, population, token, view]);

  useEffect(() => {
    void loadTrends();
  }, [loadTrends]);

  const handleSelectWeek = (weekStart: string | null) => {
    updateFilters({ week: weekStart, page: 1 });
  };

  const handleShiftWeek = (weeks: number) => {
    if (!effectiveWeek) return;
    handleSelectWeek(shiftWeekStart(effectiveWeek, weeks));
  };

  const handleSelectCell = (cell: RecognitionOutcomeCell) => {
    updateFilters({ cell: selectedCell === cell ? null : cell, page: 1 });
  };

  const handleToggleBucket = (bucket: string) => {
    setExpandedBuckets((current) => {
      const next = new Set(current);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  };

  const handleSetSamplingStrategy = async (
    unit: RecognitionCaptureUnitRow,
    strategy: RecognitionSamplingStrategy
  ) => {
    if (!hasInternalAccess || unit.samplingStrategy === strategy) return;
    const key = unitKey(unit);
    setSavingUnitKey(key);
    try {
      await ForgeApiService.setRecognitionSamplingStrategy(token, chainId, {
        image_id: unit.imageId,
        mod_tag: unit.modTag,
        strategy,
      });
      setAggregates((current) =>
        current
          ? {
              ...current,
              units: current.units.map((row) =>
                unitKey(row) === key ? { ...row, samplingStrategy: strategy } : row
              ),
            }
          : current
      );
      showSuccessToast(`Capture unit tagged ${SAMPLING_STRATEGY_LABELS[strategy].toLowerCase()}`);
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating the sampling strategy'
      );
    } finally {
      setSavingUnitKey(null);
    }
  };

  const units = useMemo(() => aggregates?.units ?? [], [aggregates]);
  const filteredUnits = useMemo(
    () => (selectedCell ? units.filter((unit) => (unit.cells[selectedCell] ?? 0) > 0) : units),
    [selectedCell, units]
  );
  const pageCount = Math.max(1, Math.ceil(filteredUnits.length / UNIT_PAGE_LIMIT));
  const currentPage = Math.min(page, pageCount);
  const visibleUnits = filteredUnits.slice(
    (currentPage - 1) * UNIT_PAGE_LIMIT,
    currentPage * UNIT_PAGE_LIMIT
  );

  const reviewedGrid = aggregates?.reviewedGrid ?? null;
  const completeness = aggregates?.reviewCompletenessPct ?? null;
  const weekIsProvisional = aggregates ? isProvisional(aggregates) : false;
  const untaggedSamples = aggregates?.population.untagged ?? 0;
  // Tagging is applied per capture unit, so the backlog headline counts units; the sample counts
  // from `population` stay as secondary detail because they size the work behind each unit.
  const untaggedUnits = aggregates?.untaggedUnitCount ?? 0;
  const showUntaggedBacklog = Boolean(aggregates) && units.length === 0 && untaggedUnits > 0;
  // `population.all` does not exist on the wire: the "all" population is the week's whole sample
  // set, which the backend reports as `total`.
  const selectedPopulationTotal = aggregates
    ? population === 'all'
      ? aggregates.population.total
      : aggregates.population.representative
    : 0;
  const reviewedSamples =
    completeness === null ? null : Math.round((completeness / 100) * selectedPopulationTotal);

  const heroMetrics: Array<{ label: string; value: string; title?: string }> = [
    {
      label: 'Confident correct rate',
      value: formatRate(aggregates?.confidentCorrectRate),
      title: 'Share of scoreable reviewed samples that recognized confidently and correctly',
    },
    {
      label: 'Reviewed samples',
      value:
        reviewedSamples === null
          ? '-'
          : `${formatNumber(reviewedSamples)} / ${formatNumber(selectedPopulationTotal)}`,
      title: `Reviewed samples out of the ${population} population this week`,
    },
    {
      label: 'Review completeness',
      value: formatPct(completeness),
      title: `Marked provisional below ${PROVISIONAL_COMPLETENESS_PCT}% completeness`,
    },
    {
      label: 'Covered',
      value: reviewedGrid ? formatPct(coveragePct(reviewedGrid)) : '-',
      title: 'Share of reviewed samples that produced a scoreable Recognition confidence outcome',
    },
    {
      label: 'Coverage failures',
      value: formatNumber(reviewedGrid?.coverageFailures),
      title: 'Reviewed samples with no scoreable Recognition result',
    },
    {
      label: 'Untagged units',
      value: formatNumber(untaggedUnits),
      title: `Capture units still holding an untagged sample (${formatNumber(
        untaggedSamples
      )} samples)`,
    },
  ];

  const trendPoints = (valueOf: (week: TrendWeek) => number | null): VersionedTrendPoint[] =>
    (trendWeeks ?? []).map((week) => ({
      timestamp: trendTimestamp(week.weekStart),
      value: valueOf(week),
      versions: trendVersions(week),
      detail: [
        `Review completeness ${formatPct(week.aggregates.reviewCompletenessPct)}`,
        `${formatNumber(week.aggregates.reviewedGrid.scoreable)} scoreable`,
      ],
    }));

  const topBuckets = useMemo(() => {
    const totals = new Map<string, number>();
    for (const week of trendWeeks ?? []) {
      for (const entry of week.aggregates.bucketPareto) {
        totals.set(entry.bucket, (totals.get(entry.bucket) ?? 0) + entry.count);
      }
    }
    return [...totals.entries()]
      .sort((left, right) => right[1] - left[1])
      .slice(0, 5)
      .map(([bucket]) => bucket);
  }, [trendWeeks]);

  const renderDetailView = () => {
    if (isLoading && !aggregates) {
      return <div className="dg-loading-state">Loading Recognition OKRs...</div>;
    }
    if (loadError) {
      return (
        <div className="dg-empty-state">
          <strong>Recognition OKRs could not be loaded</strong>
          <span>{loadError}</span>
          <button
            type="button"
            className="dg-secondary-button"
            onClick={() => void loadAggregates()}
          >
            Retry
          </button>
        </div>
      );
    }
    if (!aggregates || !reviewedGrid) return null;

    return (
      <div
        className={`recognition-okrs-detail forge-busy-container${
          isRefreshingWeek ? ' is-busy' : ''
        }`}
        aria-busy={isRefreshingWeek}
        data-testid="recognition-okrs-detail"
      >
        {isRefreshingWeek ? (
          <div className="forge-busy-overlay" role="status" aria-live="polite">
            <span
              className="forge-busy-overlay-badge"
              data-testid="recognition-okrs-updating-indicator"
            >
              Updating week...
            </span>
          </div>
        ) : null}
        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
            <span>Recognition confidence outcomes</span>
            {selectedCell ? (
              <button
                type="button"
                className="recognition-okrs-filter-chip"
                aria-label={`Clear the ${RECOGNITION_CELL_LABELS[selectedCell]} filter`}
                onClick={() => updateFilters({ cell: null, page: 1 })}
              >
                {RECOGNITION_CELL_LABELS[selectedCell]}
                <CloseOutlinedIcon fontSize="inherit" />
              </button>
            ) : null}
          </div>
          <div className="recognition-okrs-grids">
            <OutcomeGridCard
              title="Reviewed"
              description="Stored reviewer verdicts - the OKR reading"
              grid={reviewedGrid}
              isEmphasized
              selectedCell={selectedCell}
              onSelectCell={handleSelectCell}
              testId="recognition-okrs-reviewed-grid"
              note={
                <span className="recognition-okrs-grid-note">
                  Missing scan is structurally 0 here: a reviewed sample always has a verified
                  identity, so scan volume shows up in the adjustment reasons instead.
                </span>
              }
            />
            <OutcomeGridCard
              title="Raw"
              description="Recomputed against the collector's scan"
              grid={aggregates.rawGrid}
              selectedCell={selectedCell}
              onSelectCell={handleSelectCell}
              testId="recognition-okrs-raw-grid"
            />
          </div>
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title">Adjustment reasons</div>
          <div className="recognition-okrs-reasons">
            {ADJUSTMENT_REASON_ORDER.map((reason) => (
              <div key={reason} className="recognition-okrs-reason-tile">
                <span className="recognition-okrs-reason-label">
                  {ADJUSTMENT_REASON_LABELS[reason]}
                </span>
                <strong>{formatNumber(aggregates.adjustmentReasons[reason])}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
            <span>Recognition issue buckets</span>
            <span className="recognition-okrs-hint" data-testid="recognition-okrs-pareto-subtitle">
              {selectedCell
                ? `Primary buckets · ${RECOGNITION_CELL_LABELS[
                    selectedCell
                  ].toLowerCase()} — counted from the reviewed verdicts in that cell, sorted by volume.`
                : "Primary buckets across the week's reviewed samples, sorted by volume."}
            </span>
          </div>
          <BucketPareto
            entries={aggregates.bucketPareto}
            selectedCell={selectedCell}
            expandedBuckets={expandedBuckets}
            onToggleBucket={handleToggleBucket}
          />
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
            <span>Capture units</span>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={currentPage <= 1}
                onClick={() => updateFilters({ page: Math.max(1, currentPage - 1) })}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>
                Page {formatNumber(currentPage)} of {formatNumber(pageCount)} ·{' '}
                {formatNumber(filteredUnits.length)} units
              </span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={currentPage >= pageCount}
                onClick={() => updateFilters({ page: currentPage + 1 })}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
          </div>
          {showUntaggedBacklog ? (
            <div
              className="recognition-okrs-backlog"
              role="status"
              data-testid="recognition-okrs-untagged-backlog"
            >
              <strong>{formatNumber(untaggedUnits)} units await tagging</strong>
              <span>
                No capture unit in this week carries a sampling strategy yet, so the{' '}
                {population === 'all' ? 'selected' : 'representative'} population is empty. Those
                units hold {formatNumber(untaggedSamples)} of the week&apos;s{' '}
                {formatNumber(aggregates.population.total)} samples. Tag units representative or
                targeted in the Recognition review tool, or switch the population to All to score
                every capture unit.
              </span>
              <div className="recognition-okrs-backlog-actions">
                {population !== 'all' ? (
                  <button
                    type="button"
                    className="dg-secondary-button"
                    onClick={() => updateFilters({ population: 'all', page: 1 })}
                  >
                    Show all capture units
                  </button>
                ) : null}
                <Link
                  className="dg-primary-button"
                  to={`${FORGE_RECOGNITION_REVIEW_PATH}?chain=${chainId}${
                    effectiveWeek ? `&week=${effectiveWeek}` : ''
                  }`}
                >
                  Open Recognition review
                </Link>
              </div>
            </div>
          ) : filteredUnits.length === 0 ? (
            <div className="dg-empty-state">No capture units match this view.</div>
          ) : (
            <div className="dg-group-table-scroll">
              <table
                className="dg-group-table recognition-okrs-units-table"
                aria-label="Capture units"
              >
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Store</th>
                    <th>Polygons</th>
                    <th>Outcomes</th>
                    <th>Triage</th>
                    <th>Covered</th>
                    <th>Scan issues</th>
                    <th>Reviewed</th>
                    <th>Collectors</th>
                    <th>Sampling</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUnits.map((unit) => {
                    const key = unitKey(unit);
                    const scoredSamples = RECOGNITION_CELL_ORDER.reduce(
                      (sum, outcomeCell) => sum + (unit.cells[outcomeCell] ?? 0),
                      0
                    );
                    const covered =
                      unit.reviewed > 0 ? (scoredSamples / unit.reviewed) * 100 : null;
                    return (
                      <tr key={key}>
                        <td className="dg-row-id">
                          <strong>{unit.modTag ?? 'No mod tag'}</strong>
                          <em className="recognition-okrs-unit-image">image {unit.imageId}</em>
                        </td>
                        {/* A store id is an identifier, not a quantity: never locale-grouped. */}
                        <td>{unit.storeId === null ? '-' : unit.storeId}</td>
                        <td>{formatNumber(unit.total)}</td>
                        <td className="recognition-okrs-unit-bar">
                          <OutcomeDistributionBar
                            cells={unit.cells}
                            ariaLabel={`Outcomes for image ${unit.imageId}`}
                            height={10}
                          />
                        </td>
                        <td>{unit.triageScore === null ? '-' : unit.triageScore.toFixed(2)}</td>
                        <td>{formatPct(covered, 0)}</td>
                        <td>{formatNumber(unit.scanIssues)}</td>
                        <td>
                          {formatNumber(unit.reviewed)} / {formatNumber(unit.total)}
                        </td>
                        <td
                          className="recognition-okrs-unit-collectors"
                          title={unit.collectorEmails.join(', ')}
                        >
                          {unit.collectorEmails.length > 0 ? unit.collectorEmails.join(', ') : '-'}
                        </td>
                        <td>
                          <div className="recognition-okrs-strategy">
                            <span
                              className={`dg-scope-pill ${
                                unit.samplingStrategy === 'representative'
                                  ? 'is-full'
                                  : unit.samplingStrategy === 'targeted'
                                    ? 'is-partial'
                                    : 'is-unreviewed'
                              }`}
                            >
                              {SAMPLING_STRATEGY_LABELS[unit.samplingStrategy]}
                            </span>
                            {hasInternalAccess ? (
                              <select
                                aria-label={`Sampling strategy for image ${unit.imageId}`}
                                value={unit.samplingStrategy}
                                disabled={savingUnitKey === key}
                                onChange={(event) =>
                                  void handleSetSamplingStrategy(
                                    unit,
                                    event.target.value as RecognitionSamplingStrategy
                                  )
                                }
                              >
                                {unit.samplingStrategy === 'untagged' ? (
                                  <option value="untagged" disabled>
                                    Untagged
                                  </option>
                                ) : null}
                                {SETTABLE_SAMPLING_STRATEGIES.map((strategy) => (
                                  <option key={strategy} value={strategy}>
                                    {SAMPLING_STRATEGY_LABELS[strategy]}
                                  </option>
                                ))}
                              </select>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <Link
                            className="dg-id-link"
                            to={`${FORGE_RECOGNITION_REVIEW_PATH}?chain=${chainId}&image=${
                              unit.imageId
                            }${unit.modTag ? `&mod=${encodeURIComponent(unit.modTag)}` : ''}${
                              effectiveWeek ? `&week=${effectiveWeek}` : ''
                            }`}
                          >
                            Review
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  };

  const renderTrendsView = () => {
    if (isTrendsLoading && !trendWeeks) {
      return <div className="dg-loading-state">Loading Recognition OKR trends...</div>;
    }
    if (!trendWeeks || trendWeeks.length === 0) {
      return (
        <div className="dg-empty-state">
          <strong>No Recognition OKR history</strong>
          <span>{trendsError ?? 'No completed weeks were returned for this chain.'}</span>
        </div>
      );
    }

    // Same stale-data rule as the detail view: an eight-week refetch keeps the old series mounted.
    const isRefreshingTrends = isTrendsLoading && Boolean(trendWeeks);
    return (
      <div
        className={`recognition-okrs-detail forge-busy-container${
          isRefreshingTrends ? ' is-busy' : ''
        }`}
        aria-busy={isRefreshingTrends}
        data-testid="recognition-okrs-trends"
      >
        {isRefreshingTrends ? (
          <div className="forge-busy-overlay" role="status" aria-live="polite">
            <span
              className="forge-busy-overlay-badge"
              data-testid="recognition-okrs-trends-updating-indicator"
            >
              Updating trends...
            </span>
          </div>
        ) : null}
        {trendsError ? (
          <div className="recognition-okrs-hint" role="status">
            {trendsError}
          </div>
        ) : null}
        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
            <span>Confident correct rate</span>
            <span className="recognition-okrs-hint">
              Dashed segments are provisional weeks (review completeness below{' '}
              {PROVISIONAL_COMPLETENESS_PCT}%).
            </span>
          </div>
          <VersionedTrendChart
            points={trendPoints((week) =>
              week.aggregates.confidentCorrectRate === null
                ? null
                : week.aggregates.confidentCorrectRate * 100
            )}
            ariaLabel="Confident correct rate by week"
            domain={[0, 100]}
            formatValue={(value) => `${value.toFixed(0)}%`}
            formatTimestamp={(iso) => formatWeekRange(iso.slice(0, 10))}
          />
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title">Outcome mix by week</div>
          <ul className="recognition-okrs-trend-rows">
            {trendWeeks.map((week) => (
              <li key={week.weekStart}>
                <span className="recognition-okrs-trend-week">
                  {formatWeekRange(week.weekStart)}
                  {isProvisional(week.aggregates) ? <em>provisional</em> : null}
                </span>
                <OutcomeDistributionBar
                  cells={week.aggregates.reviewedGrid.cells}
                  total={week.aggregates.reviewedGrid.scoreable}
                  ariaLabel={`Outcome mix for the week of ${week.weekStart}`}
                />
                <span className="recognition-okrs-trend-value">
                  {formatRate(week.aggregates.confidentCorrectRate)}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title">
            Coverage and review progress
          </div>
          <div className="recognition-okrs-trend-charts">
            <div>
              <div className="recognition-okrs-hint">Covered</div>
              <VersionedTrendChart
                points={trendPoints((week) => coveragePct(week.aggregates.reviewedGrid))}
                ariaLabel="Coverage by week"
                domain={[0, 100]}
                formatValue={(value) => `${value.toFixed(0)}%`}
                formatTimestamp={(iso) => formatWeekRange(iso.slice(0, 10))}
              />
            </div>
            <div>
              <div className="recognition-okrs-hint">Review completeness</div>
              <VersionedTrendChart
                points={trendPoints((week) => week.aggregates.reviewCompletenessPct)}
                ariaLabel="Review completeness by week"
                domain={[0, 100]}
                formatValue={(value) => `${value.toFixed(0)}%`}
                formatTimestamp={(iso) => formatWeekRange(iso.slice(0, 10))}
              />
            </div>
          </div>
        </section>

        <section className="forge-card rdc-dashboard-card">
          <div className="forge-card-title rdc-dashboard-card-title">
            Top Recognition issue buckets
          </div>
          {topBuckets.length === 0 ? (
            <div className="dg-empty-state">No Recognition issue buckets in this window.</div>
          ) : (
            <div className="recognition-okrs-trend-charts">
              {topBuckets.map((bucket) => (
                <div key={bucket}>
                  <div className="recognition-okrs-hint">{bucketLabel(bucket)}</div>
                  <VersionedTrendChart
                    points={trendPoints((week) => {
                      const entries = week.aggregates.bucketPareto;
                      const total = entries.reduce((sum, entry) => sum + entry.count, 0);
                      if (total === 0) return null;
                      const match = entries.find((entry) => entry.bucket === bucket);
                      return ((match?.count ?? 0) / total) * 100;
                    })}
                    ariaLabel={`${bucketLabel(bucket)} share by week`}
                    domain={[0, 100]}
                    height={96}
                    formatValue={(value) => `${value.toFixed(0)}%`}
                    formatTimestamp={(iso) => formatWeekRange(iso.slice(0, 10))}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="page-content page-content--wide rdc-dashboard-page recognition-okrs-page">
      <section className="forge-card rdc-dashboard-hero">
        <div className="rdc-dashboard-hero-grid">
          <div>
            <div className="rdc-dashboard-eyebrow">Dashboards · Recognition quality</div>
            <h1 className="rdc-dashboard-title">Recognition OKRs</h1>
            <div className="rdc-dashboard-copy">
              Reviewed weekly truth for Product Recognition: how often a capture unit&apos;s
              polygons recognized confidently and correctly, and what would have to change for the
              rest.
            </div>
            <div className="recognition-okrs-crosslink">
              <Link to={FORGE_PR_INFERENCE_OKRS_PATH}>PR Inference OKRs (proxy view) ↗</Link>
              {weekIsProvisional && aggregates ? (
                <span className="dg-scope-pill is-partial">Provisional week</span>
              ) : null}
            </div>
          </div>
          {/* The KPI tiles are the loudest stale numbers on the page, so they dim with the grids
              below them; the week and population controls beside them stay live. */}
          <div
            className={`cleaning-hero-metrics forge-busy-container${
              isRefreshingWeek ? ' is-busy' : ''
            }`}
            aria-busy={isRefreshingWeek}
          >
            {heroMetrics.map((metric) => (
              <div key={metric.label} className="cleaning-metric-tile" title={metric.title}>
                <div className="cleaning-metric-label">{metric.label}</div>
                <div className="cleaning-metric-value">{metric.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="recognition-okrs-controls">
          <label className="dg-sort-control">
            <span>Chain</span>
            <select
              aria-label="Chain"
              value={chainId}
              onChange={(event) =>
                updateFilters({ chain: Number(event.target.value), cell: null, page: 1 })
              }
            >
              {FORGE_CHAIN_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className="recognition-okrs-week">
            <button
              type="button"
              className="dg-icon-button"
              aria-label="Previous week"
              disabled={!effectiveWeek}
              onClick={() => handleShiftWeek(-1)}
            >
              <ChevronLeftOutlinedIcon />
            </button>
            <label className="dg-sort-control">
              <span>Week</span>
              <input
                type="date"
                aria-label="Week start"
                value={effectiveWeek ?? ''}
                onChange={(event) => {
                  const snapped = event.target.value ? mondayOf(event.target.value) : null;
                  handleSelectWeek(snapped);
                }}
              />
            </label>
            <button
              type="button"
              className="dg-icon-button"
              aria-label="Next week"
              disabled={!effectiveWeek || Boolean(newestWeek && effectiveWeek >= newestWeek)}
              onClick={() => handleShiftWeek(1)}
            >
              <ChevronRightOutlinedIcon />
            </button>
            <span className="recognition-okrs-week-label">
              {effectiveWeek ? formatWeekRange(effectiveWeek) : '-'}
              {aggregates ? ` · ${aggregates.timeZone}` : ''}
            </span>
          </div>
          <div className="dg-scope-tabs" role="group" aria-label="Scored population">
            {POPULATION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={population === option.value ? 'is-active' : ''}
                aria-pressed={population === option.value}
                onClick={() => updateFilters({ population: option.value, cell: null, page: 1 })}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="dg-scope-tabs" role="group" aria-label="Recognition OKRs view">
            <button
              type="button"
              className={view === 'detail' ? 'is-active' : ''}
              aria-pressed={view === 'detail'}
              onClick={() => updateFilters({ view: 'detail' })}
            >
              Weekly detail
            </button>
            <button
              type="button"
              className={view === 'trends' ? 'is-active' : ''}
              aria-pressed={view === 'trends'}
              onClick={() => updateFilters({ view: 'trends' })}
            >
              Trends
            </button>
          </div>
          <ConfidenceMatrixInfo />
        </div>
      </section>

      {view === 'trends' ? renderTrendsView() : renderDetailView()}
    </div>
  );
};

export default RecognitionOkrsDashboard;
